import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  input,
  InputSignal,
  OnDestroy,
  OnInit,
  output,
  OutputEmitterRef,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import BScroll from '@better-scroll/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { PartCategory, ThreedProject } from '../../services/project-controller/project-controller';
import { ProjectControllerService } from '../../services/project-controller/project-controller.service';
import { Subject, take, takeUntil } from 'rxjs';
import { ThreeGraphqlService } from 'src/app/core/graphql/three/services/three-graphql.service';

@Component({
  selector: 'part-category-bar',
  imports: [CommonModule, MatButtonModule, MatIconModule],
  templateUrl: './part-category-bar.component.html',
  styleUrl: './part-category-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PartCategoryBarComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('listDom') listDom: ElementRef<HTMLDivElement> | undefined;

  public onClickBottomButton: OutputEmitterRef<string> = output();
  public selectedCategory: InputSignal<string> = input('');
  public project: ThreedProject | undefined;

  private betterScroll: BScroll | undefined;
  private observer: MutationObserver | undefined;
  private lastHeight: string = ''; // 마지막 height 저장

  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor(
    public threeGraphqlService: ThreeGraphqlService,
    private projectController: ProjectControllerService,
    private cd: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.projectController.project$.pipe(takeUntil(this._unsubscribeAll)).subscribe((project) => {
      if (!project) return;
      this.project = project;
      if (!this.project.partCategories) {
        this.getCategories(this.project!.templateUuid);
      }
      this.cd.markForCheck();
    });
  }

  ngAfterViewInit(): void {
    if (!this.listDom) return;

    this.betterScroll = new BScroll(this.listDom.nativeElement, {
      scrollX: true,
      probeType: 3,
      eventPassthrough: 'vertical',
      preventDefault: true,
      preventDefaultException: { tagName: /^(INPUT|TEXTAREA|BUTTON|SELECT)$/ },
    });

    this.listDom.nativeElement.addEventListener(
      'touchmove',
      (event) => {
        if (this.betterScroll?.enabled) {
          event.preventDefault();
        }
      },
      { passive: false },
    );

    this.observer = new MutationObserver(() => {
      const newHeight = this.listDom!.nativeElement.offsetHeight + 'px';
      if (this.lastHeight !== newHeight) {
        this.lastHeight = newHeight;
        this.betterScroll?.refresh();
        this.cd.markForCheck();
      }
    });

    this.observer.observe(this.listDom.nativeElement, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    this.lastHeight = this.listDom.nativeElement.offsetHeight + 'px';
  }

  ngOnDestroy(): void {
    this.betterScroll?.destroy();
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
    this._unsubscribeAll.unsubscribe();
  }

  public onClickBottomButtonHandler(value: string): void {
    this.onClickBottomButton.emit(value);
  }

  private getCategories(uuid: string): void {
    this.threeGraphqlService
      .getThreedPartCategories(this.project!.templateUuid)
      .pipe(take(1))
      .subscribe((categories: Array<PartCategory> | undefined) => {
        if (!categories || categories.length == 0) return;
        this.project!.partCategories = categories;
        this.cd.markForCheck();
      });
  }
}
